/**
 * CR323: Preview Assembler Pipeline + Source Map
 *
 * Pure, deterministic function that assembles the final deal-card render
 * configuration from the canonical layering model:
 *
 *   1. Saved Template  (template_json ⊕ overrides_json)
 *   2. Template Draft   (patch from Template Editor, optional)
 *   3. Saved Components (persisted component-level overrides)
 *   4. Component Draft  (patch from Components Studio, optional)
 *
 * The assembler is the single source of truth (SSOT) for what the preview
 * renders. It has **no side-effects**: no async, no IO, no timestamps, no
 * random IDs.
 *
 * Outputs:
 *   - finalRenderConfig  – the fully merged config object
 *   - renderSignatureHash – a stable hash of the canonicalized config
 *   - sourceMap           – which layers contributed to the result
 */

import { deepMerge } from "@/lib/deal-cards/specV2/deepMerge";
import { stableHash, stableStringify, normalizeForComparison } from "@/lib/utils/stableCompare";

// ================================================================
// PUBLIC TYPES
// ================================================================

/** Which layers the assembler is allowed to apply. */
export type PreviewMode = "saved" | "templateDraft" | "componentDraft" | "both";

/** Saved template data from the database. */
export interface SavedTemplateInput {
  template_json: Record<string, unknown> | null;
  overrides_json: Record<string, unknown> | null;
}

/** Full input contract for the assembler. */
export interface AssemblePreviewInput {
  dealTypeKey: string;
  variantKey: string;
  previewMode: PreviewMode;

  /** Saved template (template_json + overrides_json from DB) */
  savedTemplate: SavedTemplateInput;

  /** Optional patch from the Template Editor (only applied when mode includes template) */
  templateDraftPatch?: Record<string, unknown> | null;

  /** Saved component-level overrides (SSOT from DB) */
  savedComponents?: Record<string, unknown> | null;

  /** Optional patch from the Components Studio (only applied when mode includes component) */
  componentDraftPatch?: Record<string, unknown> | null;
}

/** Per-layer metadata in the source map. */
export interface LayerMeta {
  present: boolean;
  applied: boolean;
  hash: string | null;
}

/** Source map describing what contributed to the result. */
export interface PreviewSourceMap {
  inputs: {
    savedTemplate: LayerMeta;
    templateDraft: LayerMeta;
    savedComponents: LayerMeta;
    componentDraft: LayerMeta;
  };
  summary: {
    appliedLayers: string[];
    warnings: string[];
  };
}

/** Complete output of the assembler. */
export interface AssemblePreviewOutput {
  finalRenderConfig: Record<string, unknown>;
  renderSignatureHash: string;
  sourceMap: PreviewSourceMap;
}

// ================================================================
// NON-SEMANTIC KEYS (excluded from hash to prevent false positives)
// ================================================================

const NON_SEMANTIC_KEYS = new Set([
  "_debug",
  "_debugInfo",
  "_meta",
  "_timestamp",
  "_nonce",
  "_requestId",
  "savedAt",
  "lastModified",
  "updatedAt",
  "updated_at",
  "created_at",
]);

/**
 * Strip non-semantic keys from an object tree (shallow — only top-level).
 * This keeps the hash stable when debug/meta keys change.
 */
function stripNonSemanticKeys(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (!NON_SEMANTIC_KEYS.has(key)) {
      result[key] = value;
    }
  }
  return result;
}

// ================================================================
// HELPERS
// ================================================================

function hashOrNull(obj: Record<string, unknown> | null | undefined): string | null {
  if (!obj || Object.keys(obj).length === 0) return null;
  return stableHash(obj);
}

function isNonEmpty(obj: Record<string, unknown> | null | undefined): boolean {
  return obj !== null && obj !== undefined && Object.keys(obj).length > 0;
}

function shouldApplyTemplateDraft(mode: PreviewMode): boolean {
  return mode === "templateDraft" || mode === "both";
}

function shouldApplyComponentDraft(mode: PreviewMode): boolean {
  return mode === "componentDraft" || mode === "both";
}

// ================================================================
// ASSEMBLER (pure function)
// ================================================================

/**
 * Assemble the final deal-card preview config from all layers.
 *
 * This function is **pure**: same inputs always produce the same outputs.
 * It performs zero IO, async, or side effects.
 */
export function assembleDealCardPreview(input: AssemblePreviewInput): AssemblePreviewOutput {
  const {
    previewMode,
    savedTemplate,
    templateDraftPatch,
    savedComponents,
    componentDraftPatch,
  } = input;

  const warnings: string[] = [];
  const appliedLayers: string[] = [];

  // ── Layer 1: Saved Template (template_json + overrides_json) ──────────
  const baseTemplate = savedTemplate.template_json ?? {};
  const baseOverrides = savedTemplate.overrides_json ?? {};

  // Canonical merge: overrides win over template
  let config: Record<string, unknown> = deepMerge(baseTemplate, baseOverrides);
  appliedLayers.push("savedTemplate");

  if (!isNonEmpty(savedTemplate.template_json) && !isNonEmpty(savedTemplate.overrides_json)) {
    warnings.push("No saved template or overrides found — using empty base");
  }

  // ── Layer 2: Template Draft Patch ─────────────────────────────────────
  const templateDraftPresent = isNonEmpty(templateDraftPatch);
  const templateDraftApplied = templateDraftPresent && shouldApplyTemplateDraft(previewMode);

  if (templateDraftApplied) {
    config = deepMerge(config, templateDraftPatch!);
    appliedLayers.push("templateDraft");
  } else if (templateDraftPresent && !shouldApplyTemplateDraft(previewMode)) {
    warnings.push("Template draft present but not applied (mode=" + previewMode + ")");
  }

  // ── Layer 3: Saved Components ─────────────────────────────────────────
  const savedComponentsPresent = isNonEmpty(savedComponents);

  if (savedComponentsPresent) {
    config = deepMerge(config, savedComponents!);
    appliedLayers.push("savedComponents");
  }

  // ── Layer 4: Component Draft Patch ────────────────────────────────────
  const componentDraftPresent = isNonEmpty(componentDraftPatch);
  const componentDraftApplied = componentDraftPresent && shouldApplyComponentDraft(previewMode);

  if (componentDraftApplied) {
    config = deepMerge(config, componentDraftPatch!);
    appliedLayers.push("componentDraft");
  } else if (componentDraftPresent && !shouldApplyComponentDraft(previewMode)) {
    warnings.push("Component draft present but not applied (mode=" + previewMode + ")");
  }

  // ── Compute render signature hash ─────────────────────────────────────
  const semanticConfig = stripNonSemanticKeys(config);
  const renderSignatureHash = stableHash(semanticConfig);

  // ── Build source map ──────────────────────────────────────────────────
  const sourceMap: PreviewSourceMap = {
    inputs: {
      savedTemplate: {
        present: isNonEmpty(savedTemplate.template_json) || isNonEmpty(savedTemplate.overrides_json),
        applied: true, // Always applied as the base layer
        hash: hashOrNull(deepMerge(baseTemplate, baseOverrides)),
      },
      templateDraft: {
        present: templateDraftPresent,
        applied: templateDraftApplied,
        hash: hashOrNull(templateDraftPatch),
      },
      savedComponents: {
        present: savedComponentsPresent,
        applied: savedComponentsPresent, // Always applied when present
        hash: hashOrNull(savedComponents),
      },
      componentDraft: {
        present: componentDraftPresent,
        applied: componentDraftApplied,
        hash: hashOrNull(componentDraftPatch),
      },
    },
    summary: {
      appliedLayers,
      warnings,
    },
  };

  return {
    finalRenderConfig: config,
    renderSignatureHash,
    sourceMap,
  };
}
